import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeads } from '@/hooks/useLeads';
import { useDeals } from '@/hooks/useDeals';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useUnifiedMetrics } from '@/hooks/useUnifiedMetrics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Sparkles,
  Send,
  Bot,
  User,
  Zap,
  TrendingDown,
  AlertTriangle,
  DollarSign,
  Target,
  MessageSquare,
  Copy,
  Check,
  Loader2,
  Search,
  Brain,
  Plus,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { buildEliteTodayPlan } from '@/core/decision-engine/eliteEngine';
import { detectQuestionType, generateContextualResponse } from '@/lib/aiResponseEngine';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isLoading?: boolean;
}

const SUGGESTED_PROMPTS = [
  { 
    label: "What should I focus on today?", 
    icon: Zap,
    prompt: "What should I focus on today? Give me the top 3 priorities with specific actions."
  },
  { 
    label: "Which deal is most at risk?", 
    icon: TrendingDown,
    prompt: "Which deal is most at risk? Analyze my pipeline and identify deals that need immediate attention."
  },
  { 
    label: "How can I close €10k faster?", 
    icon: DollarSign,
    prompt: "How can I close €10k faster? Give me a strategic plan with specific leads to focus on."
  },
  { 
    label: "What's my pipeline health?", 
    icon: Target,
    prompt: "What's the health of my pipeline? Analyze my win rate, deal velocity, and identify gaps."
  },
];

export default function Assistant() {
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { workspace, loading: workspaceLoading } = useWorkspace();
  const { metrics } = useUnifiedMetrics();
  const { data: leads } = useLeads();
  const { data: deals } = useDeals();
  const navigate = useNavigate();

  // Handle initial prompt from navigation state - moved after generateResponse definition

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    // Only scroll to bottom when assistant responds (not when user sends message)
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.isLoading) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
  }, []);

  const generateResponse = async (prompt: string) => {
    if (!prompt.trim()) return;
    if (workspaceLoading || !workspace?.id) {
      toast.error('Workspace not ready. Please wait...');
      return;
    }

    setIsGenerating(true);
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: prompt,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    // Don't scroll here - wait for assistant response

    // Add loading message with research steps
    const loadingMessage: Message = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    };
    setMessages(prev => [...prev, loadingMessage]);

    // Simulate AI agent research process
    const researchSteps = [
      'Searching your pipeline...',
      'Analyzing deals and leads...',
      'Calculating priorities...',
      'Synthesizing insights...'
    ];

    let currentStep = 0;
    const stepInterval = setInterval(() => {
      if (currentStep < researchSteps.length) {
        setMessages(prev => prev.map(m => 
          m.id === loadingMessage.id 
            ? { ...m, content: researchSteps[currentStep] }
            : m
        ));
        currentStep++;
      } else {
        clearInterval(stepInterval);
      }
    }, 400);

    try {
      // Simulate thinking time for AI agent
      await new Promise(resolve => setTimeout(resolve, 800));

      // Use local generation (no external dependencies, no Lovable)
      const response = generateLocalResponse(prompt);

      clearInterval(stepInterval);

      // Remove loading message
      setMessages(prev => prev.filter(m => m.id !== loadingMessage.id));

      // Add assistant response
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Scroll to bottom after response
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error: any) {
      console.error('AI Assistant error:', error);
      
      // Remove loading message
      setMessages(prev => prev.filter(m => m.id !== loadingMessage.id));

      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error. Please try again or rephrase your question.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to generate response');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateLocalResponse = (prompt: string): string => {
    if (!leads || !deals || !metrics) {
      return 'No data available. Import leads to get started.';
    }

    const openLeads = leads.filter(l => 
      l.pipeline_stage !== 'won' && l.pipeline_stage !== 'lost'
    );
    const openDeals = deals.filter(d => d.stage !== 'won' && d.stage !== 'lost');
    
    // Use Elite Decision Engine for proper prioritization (same as Today page)
    const elitePlan = buildEliteTodayPlan({
      leads: openLeads,
      deals: openDeals,
    });

    // Get top priority actions from elite engine (mandatory first, then optional)
    const topActions = [
      ...(elitePlan.oneThing ? [elitePlan.oneThing] : []),
      ...elitePlan.mandatory.slice(0, 5),
      ...elitePlan.optional.slice(0, 3)
    ].slice(0, 5);

    const priorityActions = topActions.map(action => ({
      lead_id: action.leadId,
      lead_name: action.lead.name,
      title: action.reasons[0] || 'High-priority action',
      why: action.reasons.join(', ') || 'High-priority based on revenue and urgency',
      revenue_impact: action.moneyAtRisk || (action.deal?.value || 0),
      recommended_action: action.actionType,
      priority_score: action.priorityScore,
      deal_value: action.deal?.value || 0,
    }));

    // Sort by deal value first, then priority score (same logic as Today page)
    priorityActions.sort((a, b) => {
      const valueDiff = (b.deal_value || 0) - (a.deal_value || 0);
      if (Math.abs(valueDiff) > 500) return valueDiff;
      return (b.priority_score || 0) - (a.priority_score || 0);
    });

    // Use contextual AI response engine
    const questionType = detectQuestionType(prompt);
    const contextualResponse = generateContextualResponse(questionType, {
      leads: openLeads,
      deals: openDeals.map(d => ({ id: d.id, lead_id: d.lead_id, value: d.value, stage: d.stage })),
      metrics: {
        pipelineValue: metrics.pipelineValue,
        needAction: metrics.needAction,
        overdueFollowups: metrics.overdueFollowups,
        stuckDeals: metrics.stuckDeals,
        winRate: metrics.winRate || 0,
        forecast: metrics.forecast || 0
      },
      priorityActions
    });

    return contextualResponse.daily_brief;
  };

  // Handle initial prompt from navigation state
  useEffect(() => {
    const initialPrompt = (location.state as any)?.initialPrompt;
    if (initialPrompt && messages.length === 0 && !isGenerating) {
      setInputValue(initialPrompt);
      // Auto-submit after a short delay to allow component to mount
      const timer = setTimeout(() => {
        generateResponse(initialPrompt);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleSend = async () => {
    if (!inputValue.trim() || isGenerating) return;
    await generateResponse(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyMessage = async (content: string, id: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3rem)] max-w-5xl mx-auto">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center border border-primary/20">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Decision Sales OS AI Agent</h1>
                <p className="text-xs text-muted-foreground">Autonomous AI agent that researches and analyzes your sales data</p>
              </div>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMessages([])}
                className="text-xs"
              >
                Clear chat
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 px-6 py-6">
        <div className="space-y-6 max-w-3xl mx-auto" style={{ minHeight: '100%' }}>
          {messages.length === 0 && !isGenerating && (
            <>
              {/* Empty state: No leads */}
              {(!leads || leads.length === 0) ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mx-auto mb-6 border border-primary/20">
                    <Bot className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    Add leads and I'll tell you what to do today.
                  </h2>
                  <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
                    Get started by adding your first lead or importing a CSV. Once you have leads, I'll analyze your pipeline and provide strategic insights.
                  </p>
                  
                  {/* CTA Buttons */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    <Button
                      variant="default"
                      className="justify-start gap-3 h-auto py-3 px-4"
                      onClick={() => navigate('/app/leads?action=create')}
                    >
                      <Plus className="w-4 h-4 shrink-0" />
                      <span className="text-sm">Add your first lead</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="justify-start gap-3 h-auto py-3 px-4"
                      onClick={() => navigate('/app/leads?action=import')}
                    >
                      <Upload className="w-4 h-4 shrink-0" />
                      <span className="text-sm">Import CSV</span>
                    </Button>
                  </div>
                </div>
              ) : (
                /* Normal empty state: Has leads */
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mx-auto mb-6 border border-primary/20">
                    <Bot className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-2">
                    I'm your AI Agent.
                  </h2>
                  <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
                    I autonomously research your sales pipeline, analyze patterns, and provide strategic insights. Ask me anything and I'll search through your data to give you actionable answers.
                  </p>
                  
                  {/* Suggested Prompts */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                    {SUGGESTED_PROMPTS.map((prompt, i) => {
                      const Icon = prompt.icon;
                      return (
                        <Button
                          key={i}
                          variant="outline"
                          className="justify-start gap-3 h-auto py-3 px-4 text-left hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-colors"
                          onClick={() => generateResponse(prompt.prompt)}
                          disabled={isGenerating}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="text-sm">{prompt.label}</span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {messages.map((message) => (
            <div key={message.id} className="space-y-2">
              <div className={cn(
                "flex gap-3",
                message.role === 'user' ? "justify-end" : "justify-start"
              )}>
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                
                <div className={cn(
                  "max-w-[85%] rounded-xl px-4 py-3 text-sm",
                  message.role === 'user' 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-secondary text-foreground"
                )}>
                  {message.isLoading ? (
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 animate-pulse text-primary" />
                      <span className="text-muted-foreground">{message.content || 'Researching...'}</span>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap leading-relaxed">{message.content}</div>
                  )}
                </div>

                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-1">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Copy button for assistant messages */}
              {message.role === 'assistant' && !message.isLoading && (
                <div className="flex items-center gap-2 ml-11">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => copyMessage(message.content, message.id)}
                  >
                    {copiedId === message.id ? (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-border/50 bg-card/95 backdrop-blur-sm">
        <div className="px-6 py-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex gap-3">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything... I'll research your data and provide insights"
                className="flex-1 text-sm"
                disabled={isGenerating}
              />
              <Button 
                size="icon"
                onClick={handleSend}
                disabled={!inputValue.trim() || isGenerating}
                className="shrink-0"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              I analyze your real data: {leads?.length || 0} leads, {deals?.length || 0} deals, €{metrics?.pipelineValue?.toLocaleString() || '0'} pipeline value
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

